/**
 * Flota — checklisty i serwisy po przebudowie wizualnej (`fleet.web.chkui.*`, `fleet.web.svcui.*`), doklejone w `extra.ts`.
 * `fleetChecklistsServiceUiEn` jest typowany względem `fleetChecklistsServiceUiPl` — brak klucza = błąd kompilacji.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const fleetChecklistsServiceUiPl = {
  chkui: {
    kpi: {
      today: 'Wykonane dziś',
      todayHint: 'z {n} aktywnych pojazdów',
      defects: 'Z usterką dziś',
      defectsHint: 'oraz {n} z uwagami',
      defectsNone: 'Bez usterek',
      missing: 'Bez checklisty dziś',
      missingHint: 'aktywne pojazdy bez wysłanej checklisty',
      coverage: 'Wykonanie (14 dni)',
      coverageHint: '{filled} z {total} pól pojazd × dzień',
    },
    matrix: {
      title: 'Wykonanie checklist: pojazd × dzień',
      description: 'Najgorszy wynik pojazdu danego dnia, ostatnie {n} dni. Kliknij tablicę, aby przefiltrować listę.',
      legend: 'Legenda',
      level: { OK: 'W porządku', WARNING: 'Uwagi', DEFECT: 'Usterka', CRITICAL: 'Usterka krytyczna' },
      none: 'Brak checklisty',
      runs: '{count} szt.',
      empty: 'Brak pojazdów w Twoim zakresie.',
      filterVehicle: 'Filtruj listę po pojeździe {plate}',
      more: 'Pokaż pozostałe pojazdy ({n})',
      less: 'Pokaż mniej',
    },
    detail: {
      photosShort: 'Zdjęcia',
      summaryAria: 'Podsumowanie wyników punktów',
      criticalBanner: 'Usterka w punkcie krytycznym: {count}. Pojazd mógł zostać zablokowany — sprawdź zgłoszenie.',
      criticalDefect: 'Usterka w punkcie krytycznym — wymaga reakcji przed jazdą.',
      criticalHint: 'Punkt krytyczny: usterka uruchamia procedurę poważnej usterki',
    },
    editor: {
      dragHandle: 'Przeciągnij pytanie {n}, aby zmienić kolejność',
      dragHint: 'Przeciągnij, aby zmienić kolejność (albo użyj strzałek)',
    },
    phone: {
      title: 'Podgląd na telefonie kierowcy',
      caption: 'Tak kierowca zobaczy tę checklistę',
      untitled: 'Nazwa checklisty',
      progress: '{done}/{total} punktów',
      empty: 'Dodaj pytania, aby zobaczyć podgląd.',
      okShort: 'OK',
      warnShort: 'Uwaga',
      defectShort: 'Usterka',
      show: 'Pokaż podgląd na telefonie',
      hide: 'Ukryj podgląd',
      req: { NEVER: 'Nie wymagane', ON_ISSUE: 'przy uwadze/usterce', ALWAYS: 'wymagane' },
    },
    templates: { criticalCount: 'Krytyczne: {count}' },
    versions: {
      first: 'Pierwsza wersja szablonu.',
      noChanges: 'Bez zmian w pytaniach (zmiana nazwy lub typów pojazdów).',
      added: 'Dodane pytanie',
      removed: 'Usunięte pytanie',
      changed: 'Zmienione wymagania',
      empty: 'Brak wersji do pokazania.',
    },
  },
  svcui: {
    overview: 'Przegląd kosztów i terminów',
    period: 'Okres zestawienia',
    months: 'Ostatnie {n} mies.',
    view: 'Widok listy',
    viewTable: 'Tabela',
    viewTimeline: 'Oś czasu',
    kpi: {
      cost: 'Koszt serwisów ({n} mies.)',
      costHint: 'z {count} wykonanych wpisów',
      costIncomplete: 'orientacyjnie: {count} wpisów bez kosztu lub w innej walucie',
      inService: 'W serwisie teraz',
      inServiceHint: 'zaplanowane: {planned}',
      due: 'Terminy ≤ 30 dni',
      dueNone: 'Brak zaplanowanych terminów',
      avg: 'Średni koszt wpisu',
      avgHint: 'tylko wpisy z kosztem w PLN',
    },
    chart: {
      byType: 'Koszty wg typu',
      byTypeHint: 'Wykonane wpisy w PLN; po prawej kwota i liczba wpisów',
      byVehicle: 'Koszty wg pojazdu',
      byVehicleHint: 'Osiem pojazdów o najwyższych kosztach',
      byMonth: 'Koszty miesięcznie',
      byMonthHint: 'Wykonane wpisy w PLN',
      byMonthAria: 'Koszty serwisów miesięcznie, od {from} do {to}',
      empty: 'Brak wykonanych wpisów z kosztem w tym okresie.',
      emptyMonths: 'Za mało danych na wykres miesięczny.',
    },
    upcoming: {
      title: 'Najbliższe terminy serwisów',
      description: 'Z ostatniego wykonanego wpisu każdego typu dla pojazdu (data lub przebieg)',
      empty: 'Żaden wykonany wpis nie ma ustawionego następnego terminu.',
      kmLeft: 'zostało {km} km',
      kmOver: 'przekroczono o {km} km',
    },
    timeline: { open: 'Otwórz wpis: {plate}, {type}' },
    form: {
      basics: 'Co i kiedy',
      execution: 'Wykonanie',
      costs: 'Koszty i dokument',
      next: 'Następny termin',
      documents: 'Załączniki',
    },
  },
};

export const fleetChecklistsServiceUiEn: Shape<typeof fleetChecklistsServiceUiPl> = {
  chkui: {
    kpi: {
      today: 'Completed today',
      todayHint: 'of {n} active vehicles',
      defects: 'With a defect today',
      defectsHint: 'plus {n} with remarks',
      defectsNone: 'No defects',
      missing: 'No checklist today',
      missingHint: 'active vehicles without a submitted checklist',
      coverage: 'Completion (14 days)',
      coverageHint: '{filled} of {total} vehicle × day cells',
    },
    matrix: {
      title: 'Checklist completion: vehicle × day',
      description: 'Worst result per vehicle and day, last {n} days. Click a plate to filter the list.',
      legend: 'Legend',
      level: { OK: 'OK', WARNING: 'Remarks', DEFECT: 'Defect', CRITICAL: 'Critical defect' },
      none: 'No checklist',
      runs: '{count} pcs',
      empty: 'No vehicles in your scope.',
      filterVehicle: 'Filter the list by vehicle {plate}',
      more: 'Show remaining vehicles ({n})',
      less: 'Show fewer',
    },
    detail: {
      photosShort: 'Photos',
      summaryAria: 'Summary of item results',
      criticalBanner: 'Defects on critical items: {count}. The vehicle may have been blocked — check the incident.',
      criticalDefect: 'Defect on a critical item — needs action before driving.',
      criticalHint: 'Critical item: a defect triggers the serious-defect procedure',
    },
    editor: {
      dragHandle: 'Drag question {n} to reorder',
      dragHint: 'Drag to reorder (or use the arrows)',
    },
    phone: {
      title: 'Driver phone preview',
      caption: 'This is how the driver will see this checklist',
      untitled: 'Checklist name',
      progress: '{done}/{total} items',
      empty: 'Add questions to see the preview.',
      okShort: 'OK',
      warnShort: 'Remark',
      defectShort: 'Defect',
      show: 'Show phone preview',
      hide: 'Hide preview',
      req: { NEVER: 'Not required', ON_ISSUE: 'on remark/defect', ALWAYS: 'required' },
    },
    templates: { criticalCount: 'Critical: {count}' },
    versions: {
      first: 'First version of the template.',
      noChanges: 'No question changes (name or vehicle types only).',
      added: 'Question added',
      removed: 'Question removed',
      changed: 'Requirements changed',
      empty: 'No versions to show.',
    },
  },
  svcui: {
    overview: 'Costs and due dates overview',
    period: 'Summary period',
    months: 'Last {n} mo.',
    view: 'List view',
    viewTable: 'Table',
    viewTimeline: 'Timeline',
    kpi: {
      cost: 'Service cost ({n} mo.)',
      costHint: 'from {count} completed entries',
      costIncomplete: 'indicative: {count} entries without a cost or in another currency',
      inService: 'In service now',
      inServiceHint: 'planned: {planned}',
      due: 'Due within 30 days',
      dueNone: 'No scheduled due dates',
      avg: 'Average cost per entry',
      avgHint: 'only entries with a PLN cost',
    },
    chart: {
      byType: 'Cost by type',
      byTypeHint: 'Completed entries in PLN; amount and entry count on the right',
      byVehicle: 'Cost by vehicle',
      byVehicleHint: 'Eight vehicles with the highest cost',
      byMonth: 'Monthly cost',
      byMonthHint: 'Completed entries in PLN',
      byMonthAria: 'Monthly service cost, from {from} to {to}',
      empty: 'No completed entries with a cost in this period.',
      emptyMonths: 'Not enough data for a monthly chart.',
    },
    upcoming: {
      title: 'Upcoming service due dates',
      description: 'From the latest completed entry of each type per vehicle (date or mileage)',
      empty: 'No completed entry has a next due date set.',
      kmLeft: '{km} km left',
      kmOver: 'over by {km} km',
    },
    timeline: { open: 'Open entry: {plate}, {type}' },
    form: {
      basics: 'What and when',
      execution: 'Execution',
      costs: 'Cost and document',
      next: 'Next due',
      documents: 'Attachments',
    },
  },
};
